/**
 * Bridges entre los providers concretos (MSAL / Local) y el AuthContext unificado.
 * Cada bridge llama UN solo hook (cumple reglas de React).
 */

import { useEffect, useState, type ReactNode } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { Spinner } from "@fluentui/react-components";
import { loginRequest } from "./msalConfig";
import { useLocalAuth } from "./LocalAuthProvider";
import { AuthContext, type AuthUser, type AuthState } from "./useAuth";
import type { Role } from "@constants/roles";
import { buildRepos } from "../data/repos";
import { saveIdentity, loadIdentity, clearIdentity } from "../offline/identityCache";
import { purgarLocal } from "../offline/purgeLocal";
import { esErrorDeRed } from "../offline/connectivity";
import { DEPLOYMENT } from "@core/access/deployment";

interface BridgeProps {
  children: ReactNode;
}

/** Etiqueta de datos del PMIS (no es el tenant de Microsoft). */
const TENANT_PMIS = DEPLOYMENT.tenantId;

export function MsalAuthBridge({ children }: BridgeProps) {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const account = accounts[0];
  const email = account?.username ?? "";
  const name = account?.name ?? account?.username ?? "";

  // El rol NO viene del token: se resuelve desde la lista PmisUsuarios por correo
  // (vía el transporte activo — Graph en host externo). Mientras resuelve, spinner.
  const [role, setRole] = useState<Role | null>(null);
  const [resolviendo, setResolviendo] = useState(true);

  useEffect(() => {
    if (!email) {
      setResolviendo(false);
      return;
    }
    let vivo = true;
    setResolviendo(true);
    void (async () => {
      try {
        const usuarios = await buildRepos(TENANT_PMIS).usuario.list();
        const u = usuarios.find((x) => x.email.toLowerCase() === email.toLowerCase());
        if (!vivo) return;
        const r = u?.rol ?? null;
        setRole(r);
        saveIdentity({ email, name, role: r, tenantId: TENANT_PMIS });
      } catch (e) {
        // Red intermitente → conservar el rol del caché previo (mismo usuario).
        if (!vivo) return;
        if (esErrorDeRed(e)) {
          const cached = loadIdentity(TENANT_PMIS);
          if (cached && cached.email.toLowerCase() === email.toLowerCase()) setRole(cached.role);
        }
      } finally {
        if (vivo) setResolviendo(false);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [email, name]);

  if (isAuthenticated && account && resolviendo) {
    return (
      <div style={{ display: "grid", placeItems: "center", minHeight: "60vh" }}>
        <Spinner label="Verificando autorización…" />
      </div>
    );
  }

  const user: AuthUser | null = account
    ? { email, name, role, tenantId: TENANT_PMIS }
    : null;

  const value: AuthState = {
    user,
    isAuthenticated,
    login: () => instance.loginRedirect(loginRequest),
    logout: () => {
      clearIdentity(TENANT_PMIS);
      // Limpia datos locales antes de cerrar la sesión Azure AD.
      return purgarLocal().then(() => instance.logoutRedirect());
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function LocalAuthBridge({ children }: BridgeProps) {
  const local = useLocalAuth();

  const user: AuthUser | null = local.user
    ? {
        email: local.user.email,
        name: local.user.name,
        role: local.user.role,
        tenantId: local.tenantId,
      }
    : null;

  const value: AuthState = {
    user,
    isAuthenticated: local.isAuthenticated,
    login: () => Promise.resolve(),
    logout: () => {
      local.logout();
      return Promise.resolve();
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
